import type {
	CurrentContextResponse,
	HeartbeatResponse,
	AuthenticateUserPostResponseUnknown
} from '@maple-tech/aspire-core-api-types/v1/session';
import type { AuthStoreState, AuthStoreActions } from './authStore.types';
import type { FetchWrapperResponse } from '@/helpers/fetchWrapper.types';
import { defineStore, type _GettersTree } from 'pinia';

import { fetchWrapper, router, baseUrl, refreshTokenUrl } from '@/helpers';

type ResetPasswordRequest =
	| ResetPasswordWhileLoggedInRequest
	| ResetPasswordWithPasscodeRequest;

type ResetPasswordWithPasscodeRequest = {
	login: string;
	newpassword: string;
	confirmpassword: string;
	passcode: string;
};

type ResetPasswordWhileLoggedInRequest = {
	newpassword: string;
	confirmpassword: string;
};

export const useAuthStore = defineStore<
	'auth',
	AuthStoreState,
	_GettersTree<AuthStoreState>,
	AuthStoreActions
>({
	id: 'auth',
	state: () => ({
		authToken: null,
		refreshTokenTimeout: null,
		mfaMethods: null,
		signInState: 'init',
		resetRequired: false,
		sessionTimeout: null,
		timeZoneOffset: null,
		currentUserInfo: null,
		resetPasswordByPasscode: {
			login: null
		}
	}),
	actions: {
		async login(login: string, password: string) {
			const authResponse = await fetchWrapper.post(`${baseUrl}/auth`, {
				//@ts-ignore Middleware stringifies this
				login,
				password,
				timeZoneOffset: new Date().getTimezoneOffset()
			});
			await this.handleAuthResponse(authResponse);
		},
		async requestPasscode(mfaMethod: string) {
			const authResponse = await fetchWrapper.get(
				`${baseUrl}/auth/passcode/${mfaMethod}`
			);
			await this.handleAuthResponse(authResponse);
		},
		async setLoginForResetPasswordByPasscode(login: string) {
			this.resetPasswordByPasscode.login = login;
		},
		async clearLoginForResetPasswordByPasscode() {
			this.resetPasswordByPasscode.login = null;
		},
		async requestPasscodeForPasswordReset() {
			const authResponse = await fetchWrapper.put(
				`${baseUrl}/auth/password/passcode/`,
				//@ts-ignore Middleware stringifies this
				{ login: this.resetPasswordByPasscode.login }
			);
			await this.handleAuthResponse(authResponse);
		},
		async submitPasscode(passcode: string, requestmfacookie: boolean) {
			const authResponse = await fetchWrapper.put(`${baseUrl}/auth`, {
				//@ts-ignore Middleware stringifies this
				passcode,
				requestmfacookie
			});
			await this.handleAuthResponse(authResponse);
		},
		async resetPassword(
			newpassword: string,
			confirmpassword: string,
			passcode?: string
		) {
			const resetPayload = {
				newpassword,
				confirmpassword
			} as ResetPasswordRequest;
			if (passcode) {
				(resetPayload as ResetPasswordWithPasscodeRequest).passcode =
					passcode;
				(resetPayload as ResetPasswordWithPasscodeRequest).login =
					this.resetPasswordByPasscode.login || '';
			}
			const authResponse = await fetchWrapper.put(
				`${baseUrl}/auth/password`,
				//@ts-ignore Middleware stringifies this
				resetPayload
			);
			await this.handleAuthResponse(authResponse);
		},
		async requestUserNamesByEmail(email: string) {
			const authResponse = await fetchWrapper.get(
				`${baseUrl}/auth/logins/${email}`
			);
			await this.handleAuthResponse(authResponse);
		},
		async handleAuthResponse(authResponse: FetchWrapperResponse) {
			const authResponseData =
				authResponse.data as AuthenticateUserPostResponseUnknown;
			if (!authResponse.data || this.signInState === 'recoverUserName') {
				this.signInState = 'init';
				return;
			}
			const nextRoute =
				(authResponseData.next && authResponseData.next.route) ||
				authResponseData.route;
			const nextMethod =
				(authResponseData.next && authResponseData.next.method) ||
				authResponseData.method;
			const nextPayload =
				authResponseData.next && authResponseData.next.payload;

			if (authResponseData.authToken) {
				this.authToken = authResponseData.authToken;
				localStorage.setItem('token', this.authToken || '');

				if (
					nextMethod === 'get' &&
					nextRoute &&
					nextRoute.includes('/auth/passcode/:mfaMethod')
				) {
					this.signInState = 'mfaRequesting';
					if (
						authResponseData.otherInfo &&
						authResponseData.otherInfo.mfaMethods
					) {
						this.mfaMethods = {};
						if (
							authResponseData.otherInfo.mfaMethods.includes(
								'email'
							)
						) {
							this.mfaMethods.email = {
								emailAddress:
									authResponseData.otherInfo.emailAddress ||
									''
							};
						}
						if (
							authResponseData.otherInfo.mfaMethods.includes(
								'sms'
							)
						) {
							this.mfaMethods.sms = {
								phoneNumber:
									authResponseData.otherInfo.phoneNumber || ''
							};
						}
					}
				} else if (nextRoute && nextRoute.endsWith('/*')) {
					this.signInState = 'complete';
					this.startRefreshTokenTimer();
					const contextResponse = await fetchWrapper.get(
						`${baseUrl}/session/currentcontext`
					);
					if (contextResponse.data) {
						const contextResponseData =
							contextResponse.data as CurrentContextResponse;
						this.sessionTimeout =
							(contextResponseData.sessionTimeoutMinutes || 10) *
							1000;
						this.timeZoneOffset =
							(contextResponseData.timeZoneOffsetMinutes || 0) *
							1000;
						this.currentUserInfo =
							contextResponseData.currentUserInfo || null;
					}
					// CLASSIC ASPIRE SHIM
					await fetchWrapper.get(
						`${import.meta.env.VITE_API_URL}/session/cookie`
					);
					location.replace(location.pathname);
					// END CLASSIC ASPIRE SHIM
				} else if (
					nextRoute &&
					nextMethod === 'put' &&
					nextRoute.endsWith('/auth/password')
				) {
					this.signInState = 'resetPassword';
				} else if (
					nextRoute &&
					nextMethod === 'put' &&
					nextRoute.endsWith('/auth')
				) {
					this.signInState = 'mfaSubmitting';
				}
			} else if (
				nextRoute?.endsWith('/auth/password') &&
				Array.isArray(nextPayload) &&
				nextPayload.includes('passcode')
			) {
				this.signInState = 'resetPasswordByPasscode';
			} else {
				this.logout();
			}
		},
		logout() {
			fetchWrapper.delete(`${baseUrl}/auth`);
			this.stopRefreshTokenTimer();
			this.authToken = null;
			localStorage.removeItem('token');
			this.mfaMethods = null;
			this.signInState = 'init';
			this.sessionTimeout = null;
			this.timeZoneOffset = null;
			this.currentUserInfo = null;
			if (
				router.currentRoute.value.path !==
				import.meta.env.VITE_SIGNIN_BASEURL
			) {
				router.push(import.meta.env.VITE_SIGNIN_BASEURL);
			}
		},
		async refreshToken() {
			try {
				const refreshTokenResponse = (
					await fetchWrapper.get(refreshTokenUrl)
				).data as HeartbeatResponse;
				this.authToken = refreshTokenResponse.authToken;
				localStorage.setItem('token', this.authToken || '');
			} catch (err) {
				this.logout();
				return false;
			}
			this.stopRefreshTokenTimer();
			this.startRefreshTokenTimer();
			return true;
		},
		startRefreshTokenTimer() {
			const timeout = 60 * 1000;
			this.refreshTokenTimeout = window.setTimeout(
				this.refreshToken,
				timeout
			);
		},
		stopRefreshTokenTimer() {
			if (this.refreshTokenTimeout) {
				clearTimeout(this.refreshTokenTimeout);
				this.refreshTokenTimeout = null;
			}
		},
		getTokenFromLocalStorage() {
			const authToken = localStorage.getItem('token');
			if (authToken) {
				this.authToken = authToken;
			}
		}
	}
});
